import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

const isBrowser = typeof window !== 'undefined'
const TREASURY_PROXY = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TREASURY_PROXY_URL) ? import.meta.env.VITE_TREASURY_PROXY_URL : ''
const COINGECKO_PROXY = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_COINGECKO_PROXY_URL) ? import.meta.env.VITE_COINGECKO_PROXY_URL : ''

const getCached = (key, maxAgeMs) => {
  if (!isBrowser) return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.timestamp || Date.now() - parsed.timestamp > maxAgeMs) return null
    return parsed.data ?? null
  } catch {
    return null
  }
}

const setCached = (key, data) => {
  if (!isBrowser) return
  try {
    window.localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }))
  } catch {
    // ignore cache write errors
  }
}

const fetchWithTimeout = async (url, options = {}, timeoutMs = 6000) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(url, { ...options, signal: controller.signal })
    return res
  } finally {
    clearTimeout(timer)
  }
}

export function parseBlockHeight(text) {
  if (text == null) return null
  const n = Number.parseInt(String(text).trim(), 10)
  return Number.isNaN(n) ? null : n
}

export function normalizeTransaction(rawTx, seq) {
  let valueSats = 0
  if (Array.isArray(rawTx.out)) {
    for (const output of rawTx.out) {
      valueSats += output.value || 0
    }
  }
  const size = rawTx.size || rawTx.vsize || 250
  const hash = rawTx.hash || null
  return {
    key: hash ? `tx-${hash}` : `tx-seq-${seq}`,
    hash: hash || 'unknown',
    value: valueSats / 100000000,
    size,
    time: new Date(),
    inputs: rawTx.inputs?.length || 0,
    outputs: rawTx.out?.length || 0,
  }
}

export async function fetchBitcoinPriceSnapshot(lastKnown = null) {
  try {
    // Primary: CoinGecko proxy worker
    if (COINGECKO_PROXY) {
      try {
        const proxyRes = await fetchWithTimeout(`${COINGECKO_PROXY.replace(/\/$/, '')}/price`)
        if (proxyRes.ok) {
          const data = await proxyRes.json()
          const price = data.price ?? null
          const change = data.change24h ?? null
          if (price !== null) {
            return { price, priceChange: change }
          }
        }
      } catch {
        // fall through to direct CoinGecko
      }
    }

    // Fallback: direct CoinGecko API
    const res = await fetchWithTimeout('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true')
    if (res.ok) {
      const data = await res.json()
      const cgPrice = data.bitcoin?.usd ?? null
      const cgChange = data.bitcoin?.usd_24h_change ?? null
      if (cgPrice !== null) {
        return { price: cgPrice, priceChange: cgChange }
      }
    }

    // Fallback: Coinbase API
    const cbRes = await fetchWithTimeout('https://api.coinbase.com/v2/prices/BTC-USD/spot')
    if (cbRes.ok) {
      const data = await cbRes.json()
      const cbPrice = Number.parseFloat(data?.data?.amount)
      if (!Number.isNaN(cbPrice)) {
        return { price: cbPrice, priceChange: lastKnown?.priceChange ?? null }
      }
    }
    if (lastKnown) return lastKnown
    throw new Error('No price data')
  } catch (e) {
    if (lastKnown) return lastKnown
    throw e
  }
}

export async function fetchMempoolSnapshot() {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 8000)
  const opts = { signal: controller.signal }
  try {
    const [heightR, feesR, mempoolR, hashrateR, diffR] = await Promise.allSettled([
      fetch('https://mempool.space/api/blocks/tip/height', opts),
      fetch('https://mempool.space/api/v1/fees/recommended', opts),
      fetch('https://mempool.space/api/mempool', opts),
      fetch('https://mempool.space/api/v1/mining/hashrate/3d', opts),
      fetch('https://mempool.space/api/v1/difficulty-adjustment', opts),
    ])

    const okValue = (r) => (r.status === 'fulfilled' && r.value?.ok ? r.value : null)
    const heightRes = okValue(heightR)
    const feesRes = okValue(feesR)
    const mempoolRes = okValue(mempoolR)
    const hashrateRes = okValue(hashrateR)
    const diffRes = okValue(diffR)

    const blockHeight = heightRes ? parseBlockHeight(await heightRes.text()) : null
    const feeRates = feesRes ? await feesRes.json() : null
    const mempoolInfo = mempoolRes ? await mempoolRes.json() : null
    const hashrateData = hashrateRes ? await hashrateRes.json() : null
    const diffData = diffRes ? await diffRes.json() : null

    return {
      blockHeight,
      feeRates,
      mempoolInfo,
      hashrate: hashrateData?.currentHashrate ?? null,
      difficulty: diffData,
    }
  } finally {
    clearTimeout(timer)
  }
}

export async function fetchMstrPriceSnapshot(lastKnown = null) {
  // Primary: same-origin serverless function (Vercel prod) or Vite proxy (dev)
  try {
    const res = await fetchWithTimeout('/api/mstr')
    if (res?.ok) {
      const data = await res.json()
      // Vercel function returns { price, changePct }
      if (data?.price !== null && data?.price !== undefined) {
        return { price: data.price, changePct: data.changePct ?? null }
      }
      // Vite dev proxy returns raw Yahoo format
      const quote = data?.chart?.result?.[0]?.meta
      if (quote?.regularMarketPrice != null) {
        const price = quote.regularMarketPrice
        const prev = quote.previousClose ?? quote.chartPreviousClose ?? null
        const changePct = prev ? ((price - prev) / prev) * 100 : null
        return { price, changePct }
      }
    }
  } catch {
    // fall through to direct APIs
  }

  let yahooError = null
  const cgProxyBase = COINGECKO_PROXY ? COINGECKO_PROXY.replace(/\/$/, '') : null
  const treasuryProxyBase = TREASURY_PROXY ? TREASURY_PROXY.replace(/\/$/, '') : null

  // Fallback: Yahoo via CoinGecko proxy, then Treasury proxy, then direct
  try {
    const yahooUrl = cgProxyBase ? `${cgProxyBase}/mstr/yahoo`
      : treasuryProxyBase ? `${treasuryProxyBase}/mstr/yahoo`
      : 'https://query1.finance.yahoo.com/v8/finance/chart/MSTR?interval=1d&range=5d'
    const res = await fetchWithTimeout(yahooUrl)
    if (res?.ok) {
      const data = await res.json()
      const quote = data?.chart?.result?.[0]?.meta
      const price = quote?.regularMarketPrice ?? null
      const prev = quote?.previousClose ?? quote?.chartPreviousClose ?? null
      if (price !== null) {
        const changePct = prev ? ((price - prev) / prev) * 100 : null
        return { price, changePct }
      }
    }
  } catch (e) {
    yahooError = e
  }

  // Fallback: Stooq via CoinGecko proxy, then Treasury proxy, then direct
  try {
    const stooqUrl = cgProxyBase ? `${cgProxyBase}/mstr/stooq`
      : treasuryProxyBase ? `${treasuryProxyBase}/mstr/stooq`
      : 'https://stooq.pl/q/l/?s=mstr.us&f=sd2t2ohlcv&h&e=json'
    const stooqRes = await fetchWithTimeout(stooqUrl)
    if (stooqRes?.ok) {
      const data = await stooqRes.json()
      const row = Array.isArray(data) ? data[0] : data?.symbols?.[0]
      const price = row ? Number.parseFloat(row.close ?? row.c) : null
      const prev = row ? Number.parseFloat(row.previousClose ?? row.p ?? row.open ?? row.o) : null
      if (price !== null && !Number.isNaN(price)) {
        const changePct = prev && !Number.isNaN(prev) ? ((price - prev) / prev) * 100 : null
        return { price, changePct }
      }
    }
  } catch {
    // ignore, will fall back to lastKnown
  }

  if (lastKnown) return lastKnown
  if (yahooError) throw yahooError
  throw new Error('No MSTR data')
}

// Global crypto market cap + BTC dominance from CoinGecko (free, no key).
export async function fetchGlobalCryptoStats() {
  try {
    const res = await fetchWithTimeout(
      'https://api.coingecko.com/api/v3/global',
      {},
      6000
    )
    if (!res.ok) return null
    const json = await res.json()
    const d = json?.data
    if (!d) return null
    return {
      totalMarketCap: typeof d.total_market_cap?.usd === 'number' ? d.total_market_cap.usd : null,
      btcDominance: typeof d.market_cap_percentage?.btc === 'number' ? d.market_cap_percentage.btc : null,
    }
  } catch {
    return null
  }
}

// Monthly BTC/USD close history from CryptoCompare (free, no key).
export async function fetchBitcoinPriceHistory() {
  try {
    const res = await fetchWithTimeout(
      'https://min-api.cryptocompare.com/data/v2/histoday?fsym=BTC&tsym=USD&limit=2000',
      {},
      8000
    )
    if (!res.ok) return null
    const json = await res.json()
    if (json?.Response !== 'Success' || !Array.isArray(json?.Data?.Data)) return null

    // Bucket daily closes by YYYY-MM, keeping the last close of each month.
    const byMonth = new Map()
    for (const row of json.Data.Data) {
      if (typeof row?.time !== 'number' || typeof row?.close !== 'number') continue
      if (row.close <= 0) continue
      const d = new Date(row.time * 1000)
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
      byMonth.set(key, row.close) // later rows overwrite earlier ones in the same month
    }
    const series = Array.from(byMonth, ([date, price]) => ({ date, price }))
    return series.length ? series : null
  } catch {
    return null
  }
}

const RECONNECT_BASE_MS = 3000
const RECONNECT_MAX_MS = 30000

export function useBlockchainWebSocket() {
  const [transactions, setTransactions] = useState([])
  const [blocks, setBlocks] = useState([])
  const blocksRef = useRef(0)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [stats, setStats] = useState({
    txCount: 0,
    totalVolume: 0,
    avgSize: 0,
    txPerSecond: 0
  })
  
  const wsRef = useRef(null)
  const txSizesRef = useRef([])
  const startTimeRef = useRef(Date.now())
  const txCountRef = useRef(0)
  const volumeRef = useRef(0)
  const isPausedRef = useRef(false)
  const fallbackBlocksFetchedRef = useRef(false)
  const fallbackTimerRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const isUnmountingRef = useRef(false)
  const connectRef = useRef(null)

  const fetchRecentBlocksFallback = useCallback(async () => {
    if (fallbackBlocksFetchedRef.current) return
    try {
      const res = await fetch('https://mempool.space/api/blocks?limit=10')
      if (!res.ok) throw new Error('Failed fallback blocks fetch')
      const data = await res.json()
      if (Array.isArray(data) && data.length) {
        const mapped = data.map((b) => ({
          height: b.height,
          hash: b.id,
          time: new Date((b.timestamp || 0) * 1000),
          txCount: b.tx_count ?? b.nTx ?? 0,
          size: b.size,
        }))
        // merge without duplicate hashes and keep newest first
        setBlocks((prev) => {
          const existing = new Set(prev.map((b) => b.hash))
          const incoming = mapped.filter((b) => b.hash && !existing.has(b.hash))
          const combined = [...incoming, ...prev]
          const sliced = combined.slice(0, 10)
          blocksRef.current = sliced.length
          return sliced
        })
        fallbackBlocksFetchedRef.current = true
      }
    } catch (e) {
      console.error('Fallback blocks fetch failed:', e)
    }
  }, [])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (isUnmountingRef.current) return
    const existing = wsRef.current
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return
    }

    setConnectionStatus('connecting')

    try {
      const ws = new WebSocket('wss://ws.blockchain.info/inv')
      wsRef.current = ws

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0
        setConnectionStatus('connected')
        ws.send(JSON.stringify({ "op": "unconfirmed_sub" }))
        ws.send(JSON.stringify({ "op": "blocks_sub" }))
        startTimeRef.current = Date.now()
        if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current)
        fallbackTimerRef.current = setTimeout(() => {
          if (blocksRef.current === 0) {
            fetchRecentBlocksFallback()
          }
        }, 5000)
      }

      ws.onmessage = (event) => {
        if (isPausedRef.current) return

        try {
          const data = JSON.parse(event.data)
          
          if (data.op === 'utx') {
            const tx = data.x
            txCountRef.current++

            const newTx = normalizeTransaction(tx, txCountRef.current)
            volumeRef.current += newTx.value

            txSizesRef.current.push(newTx.size)
            if (txSizesRef.current.length > 100) {
              txSizesRef.current.shift()
            }

            setTransactions(prev => [newTx, ...prev.slice(0, 99)])

            const elapsed = (Date.now() - startTimeRef.current) / 1000
            const avgSize = txSizesRef.current.length > 0
              ? Math.round(txSizesRef.current.reduce((a, b) => a + b, 0) / txSizesRef.current.length)
              : 0

            setStats({
              txCount: txCountRef.current,
              totalVolume: volumeRef.current,
              avgSize,
              txPerSecond: elapsed > 0 ? txCountRef.current / elapsed : 0
            })
          }
          
          if (data.op === 'block') {
            const block = data.x
            setBlocks((prev) => {
              // skip if we already have this hash
              if (prev.some((b) => b.hash === block.hash)) {
                return prev
              }
              const next = [{
                height: block.height,
                hash: block.hash,
                time: new Date(block.time * 1000),
                txCount: block.nTx,
                size: block.size
              }, ...prev]
              const sliced = next.slice(0, 10)
              blocksRef.current = sliced.length
              return sliced
            })
            // got a block, clear fallback timer
            if (fallbackTimerRef.current) {
              clearTimeout(fallbackTimerRef.current)
              fallbackTimerRef.current = null
            }
          }
        } catch (e) {
          console.error('Parse error:', e)
        }
      }
      
      ws.onerror = () => {
        setConnectionStatus('disconnected')
        fetchRecentBlocksFallback()
        // reconnect is handled in onclose to avoid double-scheduling
      }

      ws.onclose = () => {
        setConnectionStatus('disconnected')
        fetchRecentBlocksFallback()
        if (isUnmountingRef.current) return
        clearReconnectTimer()
        const attempt = reconnectAttemptsRef.current
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS)
        reconnectAttemptsRef.current = attempt + 1
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null
          connectRef.current?.()
        }, delay)
      }
    } catch (e) {
      console.error('WebSocket error:', e)
      setConnectionStatus('disconnected')
      fetchRecentBlocksFallback()
    }
  }, [fetchRecentBlocksFallback, clearReconnectTimer])

  connectRef.current = connect

  const disconnect = useCallback(() => {
    isUnmountingRef.current = true
    clearReconnectTimer()
    if (wsRef.current) {
      // detach onclose so closing here does not schedule a reconnect
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }
  }, [clearReconnectTimer])
  
  const pause = useCallback(() => {
    isPausedRef.current = true
  }, [])
  
  const resume = useCallback(() => {
    isPausedRef.current = false
  }, [])
  
  const reset = useCallback(() => {
    setTransactions([])
    setBlocks([])
    blocksRef.current = 0
    txCountRef.current = 0
    volumeRef.current = 0
    txSizesRef.current = []
    startTimeRef.current = Date.now()
    setStats({
      txCount: 0,
      totalVolume: 0,
      avgSize: 0,
      txPerSecond: 0
    })
  }, [])
  
  useEffect(() => {
    // a remount (e.g. React StrictMode) must clear the terminal flag
    isUnmountingRef.current = false
    if (document.visibilityState === 'visible') {
      connect()
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        pause()
      } else {
        resume()
        connect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      disconnect()
    }
  }, [connect, disconnect, pause, resume])
  
  return {
    transactions,
    blocks,
    connectionStatus,
    stats,
    pause,
    resume,
    reset,
    connect,
    disconnect
  }
}

// Hook for fetching mempool.space API data
export function useMempoolData() {
  const cached = getCached('btc_mempool', 2 * 60 * 1000)

  const query = useQuery({
    queryKey: ['mempool'],
    queryFn: async () => {
      const payload = await fetchMempoolSnapshot()
      setCached('btc_mempool', payload)
      return payload
    },
    initialData: cached ?? undefined,
    staleTime: 45 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: () => document.visibilityState === 'visible' ? 45 * 1000 : false,
    retry: 1
  })

  return {
    data: query.data ?? {
      blockHeight: null,
      feeRates: null,
      mempoolInfo: null,
      hashrate: null,
      difficulty: null
    },
    error: query.error ?? null,
    isLoading: query.isLoading
  }
}

// Hook for Bitcoin price data
export function useBitcoinPrice() {
  const cached = getCached('btc_price', 5 * 60 * 1000)
  const lastGood = useRef(cached ?? null)

  const query = useQuery({
    queryKey: ['price'],
    queryFn: async () => {
      const payload = await fetchBitcoinPriceSnapshot(lastGood.current)
      lastGood.current = payload
      setCached('btc_price', payload)
      return payload
    },
    initialData: cached ?? undefined,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: () => document.visibilityState === 'visible' ? 60 * 1000 : false,
    retry: 1
  })

  return { 
    price: query.data?.price ?? null, 
    priceChange: query.data?.priceChange ?? null, 
    loading: query.isLoading,
    error: query.error ?? null
  }
}

// Hook for MSTR stock price (Yahoo w/ Stooq fallback)
export function useMstrPrice() {
  const cached = getCached('mstr_price', 5 * 60 * 1000)
  const lastGood = useRef(cached ?? null)

  const query = useQuery({
    queryKey: ['mstrPrice'],
    queryFn: async () => {
      const payload = await fetchMstrPriceSnapshot(lastGood.current)
      lastGood.current = payload
      setCached('mstr_price', payload)
      return payload
    },
    initialData: cached ?? undefined,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: () => document.visibilityState === 'visible' ? 2 * 60 * 1000 : false,
    retry: 1
  })

  return {
    price: query.data?.price ?? null,
    changePct: query.data?.changePct ?? null,
    loading: query.isLoading,
    error: query.error ?? null
  }
}

// Hook: monthly BTC price history (refreshes daily, cache + null fallback).
export function useBitcoinPriceHistory() {
  const cached = getCached('btc_price_history', 24 * 60 * 60 * 1000)

  const query = useQuery({
    queryKey: ['btcPriceHistory'],
    queryFn: async () => {
      const payload = await fetchBitcoinPriceHistory()
      if (payload) setCached('btc_price_history', payload)
      return payload
    },
    initialData: cached ?? undefined,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 48 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  return {
    history: query.data ?? cached ?? null,
    loading: query.isLoading,
    error: query.error ?? null,
  }
}

// Live Lightning Network stats from mempool.space (free, no key).
export async function fetchLightningStats() {
  try {
    const res = await fetchWithTimeout(
      'https://mempool.space/api/v1/lightning/statistics/latest',
      {},
      8000
    )
    if (!res.ok) return null
    const json = await res.json()
    const l = json?.latest
    if (!l) return null

    const nodeCount =
      typeof l.node_count === 'number'
        ? l.node_count
        : ((l.clearnet_nodes || 0) + (l.tor_nodes || 0) +
           (l.unannounced_nodes || 0) + (l.clearnet_tor_nodes || 0)) || null
    const channelCount = typeof l.channel_count === 'number' ? l.channel_count : null
    const capacityBtc =
      typeof l.total_capacity === 'number' ? l.total_capacity / 1e8 : null

    if (nodeCount === null && channelCount === null && capacityBtc === null) return null
    return { nodeCount, channelCount, capacityBtc }
  } catch {
    return null
  }
}

// Documented snapshot, last-resort fallback.
// Source: mempool.space lightning statistics, captured 2026-05-28.
const LIGHTNING_FALLBACK = { nodeCount: 12500, channelCount: 50000, capacityBtc: 5000 }

// Hook: Lightning Network stats (refreshes every 30 min, cache + static fallback).
export function useLightningStats() {
  const cached = getCached('btc_lightning_stats', 30 * 60 * 1000)

  const query = useQuery({
    queryKey: ['lightningStats'],
    queryFn: async () => {
      const payload = await fetchLightningStats()
      if (payload) setCached('btc_lightning_stats', payload)
      return payload
    },
    initialData: cached ?? undefined,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const data = query.data ?? cached ?? LIGHTNING_FALLBACK
  return {
    nodeCount: data.nodeCount ?? null,
    channelCount: data.channelCount ?? null,
    capacityBtc: data.capacityBtc ?? null,
    loading: query.isLoading,
    error: query.error ?? null,
  }
}

// Documented snapshot, last-resort fallback if API + cache both miss.
// Source: CoinGecko /global, captured 2026-05-28.
const GLOBAL_STATS_FALLBACK = { totalMarketCap: null, btcDominance: 58 }

// Hook: global crypto stats (refreshes every 5 min, cache + static fallback).
export function useGlobalCryptoStats() {
  const cached = getCached('btc_global_stats', 5 * 60 * 1000)

  const query = useQuery({
    queryKey: ['globalCryptoStats'],
    queryFn: async () => {
      const payload = await fetchGlobalCryptoStats()
      if (payload) setCached('btc_global_stats', payload)
      return payload
    },
    initialData: cached ?? undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const data = query.data ?? cached ?? GLOBAL_STATS_FALLBACK
  return {
    totalMarketCap: data.totalMarketCap ?? null,
    btcDominance: data.btcDominance ?? null,
    loading: query.isLoading,
    error: query.error ?? null,
  }
}
